/**
 * ResetPasswordPage — Landing des Links aus der Zurücksetzen-Mail
 * (`/reset-password?userId=…&token=…`). Setzt das neue Passwort und schickt zurück zur Anmeldung;
 * kein Auto-Login, weil das Backend nur ein Ergebnis liefert. Den Link prüft der Server erst beim
 * Speichern — ein abgelehnter Link bekommt danach die Seite „Dieser Link gilt nicht mehr“.
 */
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { useApi } from '@/shared/api';
import { AuthPageHeader } from './AuthPageHeader';
import { AuthStatusPage } from './AuthStatusPage';
import { authBackLinkClass } from './authLinkStyles';
import { NewPasswordForm } from './NewPasswordForm';
import { passwordFailure, type PasswordOutcome } from './passwordFailure';

export function ResetPasswordPage() {
  const api = useApi();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { userId?: string; token?: string };
  const userId = search.userId ?? '';
  const token = search.token ?? '';
  const [outcome, setOutcome] = useState<PasswordOutcome | null>(null);

  if (!userId || !token || outcome?.kind === 'expired')
    return (
      <AuthStatusPage
        eyebrow="Passwort"
        title={outcome ? 'Dieser Link gilt nicht mehr' : 'Dieser Link ist unvollständig'}
        tone={outcome ? 'error' : 'note'}
        text={
          outcome
            ? 'Der Server hat den Link abgelehnt: er ist abgelaufen oder wurde schon benutzt.'
            : 'Dem Link fehlt ein Teil. Fordere einen neuen an.'
        }
        main={{ label: 'Neuen Link anfordern', to: '/forgot-password' }}
      />
    );

  return (
    <>
      <AuthPageHeader
        eyebrow="Passwort"
        title="Neues Passwort"
        description="Danach meldest du dich mit dem neuen Passwort an."
      />
      <NewPasswordForm
        submitLabel="Passwort speichern"
        pendingLabel="Wird gespeichert …"
        failure={outcome?.kind === 'card' ? outcome : null}
        onSubmit={async (newPassword) => {
          setOutcome(null);
          try {
            await api.auth.resetPassword({ userId, token, newPassword });
            toast.success('Passwort gespeichert – melde dich jetzt neu an.');
            void navigate({ to: '/login' });
          } catch (error) {
            setOutcome(await passwordFailure(error));
          }
        }}
      />
      <Link to="/login" className={`${authBackLinkClass} self-center`}>
        Zurück zur Anmeldung
      </Link>
    </>
  );
}
